import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { TrashIcon } from "../Icons";
import { PaginationEllipsis } from "@components/Pagination";

function TableForUIPagination({
    columns,
    data,
    colSize = 12,
    maxPagesToShow = 3,
    onRowClick,
    maxRowsPerPage = 10,
    currentPage,
    setCurrentPage,
    loading = false,
    totalPages,
    totalData,
    columnConfig = {},
    hasCheckbox = false,
    hasRadioButton = false,
    hasDeleteColumn = false,
    onCheckboxChange,
    onDeleteRow,
    showPagination = true,
    selectedRows = [],
    disabledRow = [],
    onItemsPerPageChange,
    rowKey = "seq",
    shouldShowGoToPage = colSize === 6 || colSize === 8 || colSize === 12,
    tableStyle = "default",
}) {
    const { t } = useTranslation();
    const { colors } = useTheme();

    const [showSpinner, setShowSpinner] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(maxRowsPerPage);
    const [pageInput, setPageInput] = useState(String(currentPage));

    useEffect(() => {
        setPageInput(String(currentPage));
    }, [currentPage]);

    useEffect(() => {
        let showTimeout;
        let hideTimeout;

        if (loading) {
            showTimeout = setTimeout(() => setShowSpinner(true), 100);
        } else {
            hideTimeout = setTimeout(() => setShowSpinner(false), 100);
        }

        return () => {
            clearTimeout(showTimeout);
            clearTimeout(hideTimeout);
        };
    }, [loading]);

    const commitPage = () => {
        if (pageInput === "") {
            setCurrentPage(1);
            setPageInput("1");
            return;
        }

        let page = Number(pageInput);

        if (!Number.isInteger(page)) {
            setPageInput(String(currentPage));
            return;
        }

        if (page < 1) page = 1;
        if (page > totalPages) page = totalPages;

        setCurrentPage(page);
        setPageInput(String(page));
    };

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    };

    const handleNext = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    };

    const handleFirst = () => {
        setCurrentPage(1);
    };

    const handleLast = () => {
        setCurrentPage(totalPages);
    };

    const getPaginationRange = () => {
        const pages = [];

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const delta = Math.floor(maxPagesToShow / 2);
        let startPage = Math.max(1, currentPage - delta);
        let endPage = startPage + maxPagesToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = totalPages - maxPagesToShow + 1;
        }

        if (startPage > 1) {
            pages.push("...");
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < totalPages) {
            pages.push("...");
        }

        return pages;
    };

    const handleRowCheckboxChange = (row) => {
        if (onCheckboxChange) {
            const rowKeyValue = row[rowKey];
            const isSelected = selectedRows.includes(rowKeyValue);
            let newSelectedRows;

            if (isSelected) {
                newSelectedRows = selectedRows.filter((id) => id !== rowKeyValue);
            } else {
                newSelectedRows = [...selectedRows, rowKeyValue];
            }

            onCheckboxChange(newSelectedRows);
        }
    };

    const handleRowRadioButtonChange = (row) => {
        if (onCheckboxChange) {
            const rowKeyValue = row[rowKey];
            onCheckboxChange([rowKeyValue]);
        }
    };

    const handleSelectAllChange = () => {
        if (onCheckboxChange) {
            const currentPageData = data.slice((currentPage - 1) * maxRowsPerPage, currentPage * maxRowsPerPage);
            const currentPageKeys = currentPageData.map((row) => row[rowKey]);
            const allCurrentPageSelected = currentPageKeys.every((key) => selectedRows.includes(key));

            if (allCurrentPageSelected) {
                const newSelectedRows = selectedRows.filter((key) => !currentPageKeys.includes(key));
                onCheckboxChange(newSelectedRows);
            } else {
                const newSelectedRows = [...selectedRows, ...currentPageKeys.filter((key) => !selectedRows.includes(key))];
                onCheckboxChange(newSelectedRows);
            }
        }
    };

    const handleItemsPerPageChange = (selectedOption) => {
        const newItemsPerPage = parseInt(selectedOption.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        if (onItemsPerPageChange) {
            onItemsPerPageChange(newItemsPerPage);
        }
    };

    const isRowSelected = (row) => {
        const rowKeyValue = row[rowKey];
        return selectedRows.includes(rowKeyValue);
    };

    const isRowDisabled = (row) => {
        const rowKeyValue = row[rowKey];
        return disabledRow.includes(rowKeyValue);
    };

    // Calculate paginated data
    const paginatedData = data.slice((currentPage - 1) * maxRowsPerPage, currentPage * maxRowsPerPage);

    return (
        <>
            <div className="relative" data-testid="table-for-ui-pagination">
                <div className={`overflow-hidden rounded-lg border ${colors.borderPrimary}`}>
                    <div className="overflow-x-auto">
                        {/* Loading Overlay */}
                        {showSpinner && (
                            <div className={`bg-opacity-50 absolute inset-0 z-10 flex items-center justify-center ${colors.bgSecondary}`}>
                                <div className={`h-8 w-8 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                            </div>
                        )}

                        {/* Table */}
                        <table className={`w-full ${tableStyle === "default" ? "" : tableStyle}`} data-testid="table-for-ui-pagination-table">
                            <thead className={`${colors.bgPrimary}`}>
                                <tr>
                                    {hasCheckbox && (
                                        <th className="w-16 px-4 py-3 text-left">
                                            <div className="relative">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only"
                                                    checked={paginatedData.length > 0 && paginatedData.every((row) => isRowSelected(row))}
                                                    onChange={handleSelectAllChange}
                                                />
                                                <div
                                                    className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${paginatedData.length > 0 && paginatedData.every((row) => isRowSelected(row))
                                                        ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                                        : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                                        }`}
                                                    onClick={handleSelectAllChange}
                                                >
                                                    {paginatedData.length > 0 && paginatedData.every((row) => isRowSelected(row)) && (
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </th>
                                    )}
                                    {hasRadioButton && <th className="w-16 px-4 py-3"></th>}
                                    {columns.map((column, index) => {
                                        const columnKey = column;
                                        const config = columnConfig[columnKey] || {};
                                        const { headerRender, title } = config;
                                        const columnTitle = title || column;

                                        return (
                                            <th
                                                key={index}
                                                className={`${colors.textSubTitle} px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                                                title={!headerRender ? columnTitle : undefined}
                                                style={config.style}
                                            >
                                                {headerRender ? headerRender(columnKey) : columnTitle}
                                            </th>
                                        );
                                    })}
                                    {hasDeleteColumn && (
                                        <th
                                            className={`${colors.textPrimary} w-20 px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase`}
                                        >
                                            ACTION
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className={colors.bgTableBody}>
                                {!loading && paginatedData.length === 0 && (
                                    <tr>
                                        <td
                                            className={`px-4 py-8 text-center ${colors.textMuted}`}
                                            colSpan={
                                                columns.length + (hasCheckbox ? 1 : 0) + (hasRadioButton ? 1 : 0) + (hasDeleteColumn ? 1 : 0)
                                            }
                                        >
                                            {t("common.no-data-to-display")}
                                        </td>
                                    </tr>
                                )}
                                {paginatedData &&
                                    paginatedData.length > 0 &&
                                    paginatedData.map((row, index) => {
                                        const getRowStripeColor = (index) => {
                                            return index % 2 === 0 ? colors.bgTableStripe : colors.bgPrimary;
                                        };

                                        return (
                                            <tr
                                                key={index}
                                                className={` ${getRowStripeColor(index)} ${isRowSelected(row) ? `!${colors.bgSelectedRow}` : ""} ${isRowDisabled(row) ? "cursor-not-allowed opacity-50" : ""} ${onRowClick && !isRowDisabled(row) ? `cursor-pointer transition-colors ${colors.hoverTableRow}` : ""} `}
                                                onClick={onRowClick && !isRowDisabled(row) ? () => onRowClick(row) : undefined}
                                            >
                                                {hasCheckbox && (
                                                    <td className="px-4 py-3">
                                                        <div onClick={(event) => event.stopPropagation()}>
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    className="sr-only"
                                                                    checked={isRowSelected(row)}
                                                                    disabled={isRowDisabled(row)}
                                                                    onChange={() => handleRowCheckboxChange(row)}
                                                                />
                                                                <div
                                                                    className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200 ${isRowDisabled(row)
                                                                        ? `cursor-not-allowed ${colors.borderDisabledOutLineButton} ${colors.bgDisabledControl} opacity-50`
                                                                        : isRowSelected(row)
                                                                            ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                                                            : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                                                        }`}
                                                                    onClick={() => !isRowDisabled(row) && handleRowCheckboxChange(row)}
                                                                >
                                                                    {isRowSelected(row) && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
                                                                                <path
                                                                                    fillRule="evenodd"
                                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                                    clipRule="evenodd"
                                                                                />
                                                                            </svg>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                )}
                                                {hasRadioButton && (
                                                    <td className="px-4 py-3">
                                                        <div onClick={(event) => event.stopPropagation()}>
                                                            <div className="relative">
                                                                <input
                                                                    type="radio"
                                                                    className="sr-only"
                                                                    checked={isRowSelected(row)}
                                                                    disabled={isRowDisabled(row)}
                                                                    onChange={() => handleRowRadioButtonChange(row)}
                                                                />
                                                                <div
                                                                    className={`relative h-4 w-4 cursor-pointer rounded-full border-1 transition-all duration-200 ${isRowDisabled(row)
                                                                        ? `${colors.borderPrimary} opacity-50`
                                                                        : isRowSelected(row)
                                                                            ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
                                                                            : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
                                                                        }`}
                                                                    onClick={() => !isRowDisabled(row) && handleRowRadioButtonChange(row)}
                                                                >
                                                                    {isRowSelected(row) && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <div className={`${colors.bgHoverPrimary} h-2 w-2 rounded-full`}></div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                )}
                                                {columns.map((column, colIndex) => {
                                                    const columnKey = column;
                                                    const config = columnConfig[columnKey] || {};
                                                    const { customRender } = config;

                                                    return (
                                                        <td
                                                            key={colIndex}
                                                            className={`px-4 py-3 text-sm whitespace-nowrap ${colors.textPrimary}`}
                                                            style={config.style}
                                                        >
                                                            {customRender ? customRender(row[columnKey], row, columnKey, index) : row[columnKey]}
                                                        </td>
                                                    );
                                                })}
                                                {hasDeleteColumn && (
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center justify-center">
                                                            <button
                                                                type="button"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    if (onDeleteRow) {
                                                                        onDeleteRow(row);
                                                                    }
                                                                }}
                                                                className={`rounded p-1 ${colors.iconDelete} transition-colors ${colors.hoverTextDanger}`}
                                                                title="Delete"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {showPagination && paginatedData.length > 0 && (
                <div className={`${colors.bgTableBody} px-4 py-3`}>
                    <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                        {/* Go to page */}
                        {shouldShowGoToPage && totalPages > 1 && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className={`${colors.textSubTitle} text-xs font-medium`}>{t("common.go-to-page")}</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    value={pageInput}
                                    onChange={(e) => {
                                        setPageInput(e.target.value);
                                    }}
                                    onBlur={commitPage}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            commitPage();
                                            e.target.blur();
                                        }
                                    }}
                                    className={`h-8 w-16 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
                                />
                            </div>
                        )}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                {/* Previous Page Button */}
                                <button
                                    type="button"
                                    data-testid="table-for-ui-pagination-prev-button"
                                    onClick={handlePrevious}
                                    disabled={currentPage === 1}
                                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
                                    title="Previous page"
                                >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.prev")}</span>
                                </button>

                                {/* Page Numbers */}
                                <div className="flex items-center gap-1">
                                    {getPaginationRange().map((page, index) => (
                                        <div key={index}>
                                            {page === "..." ? (
                                                <PaginationEllipsis
                                                    className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`}
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    data-testid={`table-for-ui-pagination-page-${page}`}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${page === currentPage ? `${colors.bgPaginationButtonActive}` : `${colors.bgInput}`
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Next Page Button */}
                                <button
                                    type="button"
                                    data-testid="table-for-ui-pagination-next-button"
                                    onClick={handleNext}
                                    disabled={currentPage === totalPages}
                                    className={`flex items-center gap-1 p-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
                                    title="Next page"
                                >
                                    <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.next")}</span>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default TableForUIPagination; 